import React from 'react'; 
import {
  Typography,
  Box,
  Button,
  TextField,
  Paper,
  Container,
  Grid,
  IconButton,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';

import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import GavelIcon from '@mui/icons-material/Gavel';
import HandshakeIcon from '@mui/icons-material/Handshake';

const HeroSection = styled(Box)(({ theme }) => ({
  background:
    'linear-gradient(135deg, rgba(1, 183, 234, 0.92) 0%, rgba(2, 136, 209, 0.92) 100%), url("/images/donate-hero.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  padding: theme.spacing(14, 4),
  textAlign: 'center',
  position: 'relative',
  marginTop: '64px',
  borderRadius: '0 0 20px 20px',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url("/pattern.png")',
    opacity: 0.08,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1),
    backgroundColor: '#FFFFFF',
    fontSize: '0.875rem',
    padding: '6px 10px',
    '& .MuiOutlinedInput-input': {
      padding: '10px 12px',
    },
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#01B7EA',
      },
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#01B7EA',
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#01B7EA',
  },
}));

const DonationCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(0, 0, 0, 0.05)',
  backgroundColor: '#FFFFFF',
  position: 'relative',
  overflow: 'visible',
  transition: '0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
  '&:hover': {
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.06)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    background: 'linear-gradient(90deg, #01B7EA 0%, #0288D1 100%)',
    borderRadius: '8px 8px 0 0',
  },
}));

const Donate = () => {
  const impactAreas = [
    { icon: <BusinessCenterIcon sx={{ fontSize: 24 }} />, title: 'Organizational Capacity' },
    { icon: <MenuBookIcon sx={{ fontSize: 24 }} />, title: 'Research and Study' },
    { icon: <NaturePeopleIcon sx={{ fontSize: 24 }} />, title: 'Enabling Environment' },
    { icon: <GavelIcon sx={{ fontSize: 24 }} />, title: 'Good Governance' },
    { icon: <HandshakeIcon sx={{ fontSize: 24 }} />, title: 'Peace Promotion' },
  ];

  return (
    <Box>
      <HeroSection>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <IconButton
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                mb: 3,
                p: 2,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' },
              }}
            >
              <FavoriteIcon sx={{ fontSize: 40, color: '#FF4B4B' }} />
            </IconButton>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Make a Difference Today
            </Typography>
            <Typography
              variant="h5"
              sx={{
                maxWidth: '800px',
                mx: 'auto',
                opacity: 0.9,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                fontWeight: 400,
                mb: 3,
              }}
            >
              Your generous donation helps us empower communities and create lasting change.
            </Typography>
            <Chip
              label="Secure Donation"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: '#FFFFFF',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            />
          </Box>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={7}>
            <DonationCard>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <StyledTextField fullWidth label="Full Name" variant="outlined" required />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Email Address"
                    variant="outlined"
                    type="email"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField fullWidth label="Phone Number" variant="outlined" required />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Donation Amount (ETB)"
                    variant="outlined"
                    type="number"
                    required
                    inputProps={{ min: 50 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Message (Optional)"
                    variant="outlined"
                    multiline
                    rows={4}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    endIcon={<VolunteerActivismIcon />}
                    sx={{
                      bgcolor: '#01B7EA',
                      color: '#FFFFFF',
                      py: 2,
                      borderRadius: 1,
                      fontWeight: 600,
                      '&:hover': {
                        bgcolor: '#0288D1',
                      },
                    }}
                  >
                    Complete Donation
                  </Button>
                </Grid>
              </Grid>
            </DonationCard>
          </Grid>

          <Grid item xs={12} md={5}>
            <Typography
              variant="h4"
              sx={{
                mb: 3,
                fontWeight: 700,
                color: '#111827',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -6,
                  left: 0,
                  width: 60,
                  height: 3,
                  borderRadius: 2,
                  backgroundColor: '#01B7EA',
                },
              }}
            >
              Our Core Focus Areas
            </Typography>

            <Grid container spacing={2}>
              {impactAreas.map((area, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                  <Box
                    sx={{
                      backgroundColor: 'rgba(1, 183, 234, 0.08)',
                      color: '#0288D1',
                      borderRadius: '50%',
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {area.icon}
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#111827' }}>
                    {area.title}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Donate;
